/* eslint-disable jsx-a11y/media-has-caption, jsx-a11y/no-static-element-interactions, max-len */
import * as React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';

import { dataLayerEvents } from 'common/utils';
import { AUDIO_PLAYER_ANALYTICS } from 'client/constants';

import * as css from './AudioPlayer.scss';

const PLAYING_STATE = 'playingState';
const PAUSED_STATE = 'pausedState';

let isPlay25 = false;
let isPlay50 = false;
let isPlay75 = false;
let isPlay90 = false;
// let isPlay100 = false;

let wavesTimer = null;
let wavePosition;

export default class AudioPlayer extends React.PureComponent {
	static propTypes = {
		className: PropTypes.string,
		audioSrc: PropTypes.string.isRequired,
		podcastName: PropTypes.string.isRequired,
	};

	static defaultProps = {
		className: '',
	};

	constructor(props) {
		super(props);

		this.audioRef = React.createRef();
		this.pbRef = React.createRef();
		this.progressPointerRef = React.createRef();
		this.pbInRef = React.createRef();
		this.startDragPoint = { posX: 0 };

		this.state = {
			currentTime: 0,
			totalTime: -1,
			currentState: PAUSED_STATE,
			isDragging: false,
			startPlaying: false, // Analytics
			currentWavePosition: 0,
			isSpriteLoadingAllowed: false,
		};
	}

	componentDidMount() {
		if (this.audioRef && this.audioRef.current) {
			this.audioRef.current.addEventListener('canplay', this.onAudioEvent);
			this.audioRef.current.addEventListener('play', this.onAudioEvent);
			this.audioRef.current.addEventListener('pause', this.onAudioEvent);
			this.audioRef.current.addEventListener('ended', this.onAudioEvent);
			this.audioRef.current.addEventListener('timeupdate', this.onAudioEvent);

			this.audioRef.current.load();
		}

		setTimeout(() => {
			this.setState({ isSpriteLoadingAllowed: true });
		}, 500);
	}

	componentWillUnmount() {
		if (this.audioRef && this.audioRef.current) {
			this.audioRef.current.removeEventListener('canplay', this.onAudioEvent);
			this.audioRef.current.addEventListener('play', this.onAudioEvent);
			this.audioRef.current.addEventListener('pause', this.onAudioEvent);
			this.audioRef.current.removeEventListener('ended', this.onAudioEvent);
			this.audioRef.current.removeEventListener('timeupdate', this.onAudioEvent);
		}
	}

	formatSeconds = amount => {
		if (!Number.isFinite(amount) || Number.isNaN(amount)) {
			return null;
		}

		const rAmount = Math.round(amount);
		const minutes = Math.floor(rAmount / 60);
		const seconds = rAmount % 60;
		const minStr = minutes < 10 ? `0${minutes}` : `${minutes}`;
		const secStr = seconds < 10 ? `0${seconds}` : `${seconds}`;

		return `${minStr}:${secStr}`;
	};

	onAudioEvent = e => {
		const audio = this.audioRef.current;
		const { podcastName } = this.props;
		const { currentTime, totalTime } = this.state;

		if (!audio) {
			return;
		}

		switch (e.type) {
			case 'canplay':
				this.setState({ currentTime: audio.currentTime, totalTime: audio.duration });
				break;

			case 'play':
				dataLayerEvents.audioPlayerEvents(AUDIO_PLAYER_ANALYTICS.PLAYING, podcastName);
				break;

			case 'pause':
				break;

			case 'timeupdate':
				if (currentTime < totalTime * 0.25 && isPlay25) {
					isPlay25 = false;
				} else if (currentTime < totalTime * 0.5 && isPlay50) {
					isPlay50 = false;
				} else if (currentTime < totalTime * 0.75 && isPlay75) {
					isPlay75 = false;
				} else if (currentTime < totalTime * 0.9 && isPlay90) {
					isPlay90 = false;
				}
				// else if (currentTime === totalTime && isPlay100) {
				// 	isPlay100 = false;
				// }

				if (this.state.currentState !== PAUSED_STATE) {
					if (currentTime >= totalTime * 0.25 && !isPlay25) {
						isPlay25 = true;
						dataLayerEvents.podcastPercents('25%', podcastName);
					}

					if (currentTime >= totalTime * 0.5 && !isPlay50) {
						isPlay50 = true;
						dataLayerEvents.podcastPercents('50%', podcastName);
					}

					if (currentTime >= totalTime * 0.75 && !isPlay75) {
						isPlay75 = true;
						dataLayerEvents.podcastPercents('75%', podcastName);
					}

					if (currentTime >= totalTime * 0.9 && !isPlay90) {
						isPlay90 = true;
						dataLayerEvents.podcastPercents('90%', podcastName);
					}

					// if (currentTime === totalTime && !isPlay100) {
					// 	isPlay100 = true;
					// 	dataLayerEvents.podcastPercents('100%', podcastName);
					// }

					this.setState({ currentTime: audio.currentTime });
				}
				break;

			case 'ended':
				this.onEndedStartPlay();
				break;

			default:
				break;
		}
	};

	onEndedStartPlay = () => {
		const { podcastName } = this.props;
		const audio = this.audioRef.current;

		if (!audio) {
			return;
		}
		audio.play();

		dataLayerEvents.podcastPercents('100%', podcastName);
		dataLayerEvents.audioPlayerEvents(AUDIO_PLAYER_ANALYTICS.REPLAY, podcastName);
	};

	onPlayClick = () => {
		const audio = this.audioRef.current;
		const { podcastName } = this.props;
		const { currentState, startPlaying } = this.state;

		if (!audio) {
			return;
		}

		if (currentState === PLAYING_STATE) {
			audio.pause();
			this.setState({ currentState: PAUSED_STATE });
			this.animateWaveEnd();

			dataLayerEvents.audioPlayerEvents(AUDIO_PLAYER_ANALYTICS.PAUSE, podcastName);
		} else {
			// Analytics events
			if (currentState === PAUSED_STATE && startPlaying) {
				dataLayerEvents.audioPlayerEvents(AUDIO_PLAYER_ANALYTICS.UNPAUSE, podcastName);
			}

			audio.play();
			// Analytics events
			if (!startPlaying) {
				dataLayerEvents.audioPlayerEvents(AUDIO_PLAYER_ANALYTICS.START, podcastName);
				this.setState({ startPlaying: true });
			}
			this.setState({ currentState: PLAYING_STATE });
			this.animateWaveStart();
		}
	};

	animateWaveStart = () => {
		const { currentWavePosition } = this.state;
		const frameWidth = 1.694915; // 860;
		const totalWidth = 100; // 50740; // finishedPosition -50740
		const interval = 1000 / 30;

		wavePosition = currentWavePosition;

		wavesTimer = setInterval(() => {
			wavePosition += frameWidth;

			if (wavePosition <= totalWidth) {
				this.setState({ currentWavePosition: wavePosition });
			} else {
				wavePosition = 0;
				this.setState({ currentWavePosition: wavePosition });
			}
		}, interval);
	};

	animateWaveEnd = () => {
		const { currentWavePosition } = this.state;
		clearInterval(wavesTimer);
		this.setState({ currentWavePosition });
		wavePosition = 0;
	};

	onProgressBarMouseEvent = e => {
		const pbWidth = this.pbInRef.current.offsetWidth;
		let pageX;

		switch (e.type) {
			case 'touchstart':
			case 'mousedown':
				document.addEventListener('mousemove', this.onDocumentMouseEvent);
				document.addEventListener('mouseup', this.onDocumentMouseEvent);
				document.addEventListener('touchmove', this.onDocumentMouseEvent);
				document.addEventListener('touchend', this.onDocumentMouseEvent);

				pageX = _.get(e, 'touches.0.clientX', 0) || _.get(e, 'pageX');

				this.setState({ isDragging: true }, () => {
					this.startDragPoint.posX = pageX - this.pbRef.current.getBoundingClientRect().left;
					this.progressPointerRef.current.style.left = `${this.startDragPoint.posX}px`;
					this.pbInRef.current.style.transform = `scaleX(${this.startDragPoint.posX / pbWidth})`;
				});
				break;

			default:
				break;
		}
	};

	onDocumentMouseEvent = e => {
		const { podcastName } = this.props;
		const pbWidth = this.pbInRef.current.offsetWidth;
		let pbTargetPosX = 0;
		let pageX = 0;

		switch (e.type) {
			case 'mousemove':
			case 'touchmove':
			case 'touchend':
			case 'mouseup':
				pageX = _.get(e, 'touches.0.clientX', 0) || _.get(e, 'pageX');
				pbTargetPosX = pageX - this.pbRef.current.getBoundingClientRect().left;
				pbTargetPosX = Math.min(Math.max(0, pbTargetPosX), this.pbInRef.current.offsetWidth);
				this.progressPointerRef.current.style.left = `${pbTargetPosX}px`;
				this.pbInRef.current.style.transform = `scaleX(${pbTargetPosX / pbWidth})`;

				if (e.type === 'mouseup' || e.type === 'touchend') {
					this.audioRef.current.currentTime = (pbTargetPosX / pbWidth) * this.audioRef.current.duration;

					// Analytics events
					if (this.audioRef.current.currentTime < this.state.currentTime) {
						dataLayerEvents.audioPlayerEvents(AUDIO_PLAYER_ANALYTICS.SKIP_BACKWARDS, podcastName);
					} else {
						dataLayerEvents.audioPlayerEvents(AUDIO_PLAYER_ANALYTICS.SKIP_FORWARDS, podcastName);
					}

					setTimeout(() => {
						this.setState({
							isDragging: false,
							currentTime: (pbTargetPosX / pbWidth) * this.audioRef.current.duration,
						});
					}, 300);

					document.removeEventListener('mousemove', this.onDocumentMouseEvent);
					document.removeEventListener('mouseup', this.onDocumentMouseEvent);
					document.removeEventListener('touchmove', this.onDocumentMouseEvent);
					document.removeEventListener('touchend', this.onDocumentMouseEvent);
				}
				break;

			default:
				break;
		}
	};

	render() {
		const { className, audioSrc } = this.props;
		const { isSpriteLoadingAllowed } = this.state;
		const { currentTime, totalTime, currentState, isDragging, currentWavePosition } = this.state;
		const currentTimeStr = this.formatSeconds(currentTime);
		const totalTimeStr = this.formatSeconds(totalTime);
		const progress = currentTime / totalTime;

		return (
			<div
				className={classNames(css.audioPlayer, css.wrapAudio, className)}
				style={{
					backgroundImage: isSpriteLoadingAllowed ? `url(assets/client/imgs/waves_sprite.png)` : '',
					backgroundPosition: `${currentWavePosition}% 0`,
				}}
			>
				<audio className={css.audio} src={audioSrc} ref={this.audioRef} />
				<div className={css.controls}>
					<button className={css.controlBtns} onClick={this.onPlayClick} type="button">
						{currentState === PLAYING_STATE ? (
							<i className={css.pauseIcon} />
						) : (
							<i className={css.playIcon} />
						)}
					</button>
				</div>
				<div
					className={css.progressBarWrap}
					onMouseDown={this.onProgressBarMouseEvent}
					onTouchStart={this.onProgressBarMouseEvent}
					ref={this.pbRef}
				>
					<div
						className={css.progressBar}
						style={!isDragging ? { transform: `scaleX(${progress})` } : { transform: '' }}
						ref={this.pbInRef}
					/>
					<div
						className={css.progressPointer}
						style={!isDragging ? { left: `${progress * 100}%` } : { left: '' }}
						ref={this.progressPointerRef}
					/>
				</div>
				<div className={css.time}>
					<span>{totalTimeStr && `${currentTimeStr}`}</span>
					<span>{totalTimeStr && totalTimeStr}</span>
				</div>
			</div>
		);
	}
}
